import { Typography, DaysPicker, Tooltip } from '@frontend/design_system'
import { InfoIcon } from 'ui/icons'
import { useAppSelector } from 'redux/hooks'
import { Container } from 'ui/molecules/Container'
import { MENU_NAMES } from 'texts/menuNames'
import { TOOLTIP } from 'texts/dashboard'
import {
  isOperatorSelector,
  supplierCurrencySelector,
} from 'redux/login/selectors'
import { InfoBlock } from './components/InfoBlock'
import { useGeneralData } from '../hooks/useGeneralData'
import { Filter } from 'ui/atoms/Filter'
import { useFilterSettings } from 'hooks/useFilterSettings'
import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FULL_PERIOD_MAX_DURATION } from './constants'
import { selectedFiltersSelector } from 'redux/filters/selectors'
import { setSelectedDashboardRange } from 'redux/dashboard/slice'
import { differenceInDays, format, startOfYear, subYears } from 'date-fns'
import { STOP_SALES_DATE_FORMAT } from 'constants/date'
import { selectedRangeSelector } from 'redux/dashboard/selectors'
import { DASHBOARD_FILTER_KEY } from 'constants/filters'
import { ReservationsSchedule } from './components/ReservationsSchedule'
import { RevenuePerCarClass } from './components/RevenuePerCarClass'
import { RevenuePerCar } from './components/RevenuePerCar'
import { useCompanyId } from 'hooks/useCompanyId'
import { breadcrumbs } from 'constants/breadcrumbs'
import { joinClasses } from 'utils/joinClasses'
import { CAR_TYPES } from 'constants/carTypes'
import styles from './styles.module.scss'

export const DashboardList = () => {
  const supplierCurrency = useAppSelector(supplierCurrencySelector)
  const { dashboard } = useSelector(selectedFiltersSelector)
  const selectedRange = useSelector(selectedRangeSelector)
  const isOperator = useSelector(isOperatorSelector)

  const selectedCarCategories = dashboard.carCategories?.length
    ? dashboard.carCategories
    : CAR_TYPES

  const companyId = useCompanyId()

  const dispatch = useDispatch()
  const previousYear = startOfYear(subYears(new Date(), 1))

  const breadcrumbsList = [breadcrumbs.profileList]

  const { volumesData, revenueData } = useGeneralData(
    companyId,
    {
      from: selectedRange.from,
      to: selectedRange.to,
    },
    selectedCarCategories
  )

  const filterSettings = useFilterSettings(DASHBOARD_FILTER_KEY)

  const handleSelectedRange = useCallback(
    (newValues: { from: Date; to: Date }) => {
      dispatch(
        setSelectedDashboardRange({
          from: format(newValues.from, STOP_SALES_DATE_FORMAT),
          to: newValues.to ? format(newValues.to, STOP_SALES_DATE_FORMAT) : '',
        })
      )
    },
    []
  )

  const daysPickerRange = useMemo(
    () => ({
      from: selectedRange.from ? new Date(selectedRange.from) : null,
      to: selectedRange.to ? new Date(selectedRange.to) : null,
    }),
    [selectedRange]
  )

  const isShortPeriod = useMemo(
    () =>
      differenceInDays(
        new Date(selectedRange.to),
        new Date(selectedRange.from)
      ) > FULL_PERIOD_MAX_DURATION,
    [selectedRange.from, selectedRange.to]
  )

  const filters = useMemo(
    () => (
      <div
        className={joinClasses(styles.filters, [
          styles['outside-header'],
          isOperator,
        ])}
      >
        <div className={styles['date-picker']}>
          <DaysPicker
            range={daysPickerRange}
            initialFrom={selectedRange.from}
            initialTo={selectedRange.to}
            isAvailableFutureDates
            variant="dashboard"
            size="large"
            fromDate={previousYear}
            handleRangeSelect={handleSelectedRange}
          />
        </div>
        <Filter
          variant="button"
          label="Car type"
          {...filterSettings('carCategories')}
        />
      </div>
    ),
    [filterSettings, selectedRange]
  )

  return (
    <Container
      currentPageLabel="Dashboard"
      withNavigation={isOperator}
      breadcrumbList={isOperator ? breadcrumbsList : []}
      title={
        <Typography
          Tag="div"
          name="H5"
          className={joinClasses(styles['container-title'], [
            styles['with-offset'],
            !isOperator,
          ])}
        >
          {MENU_NAMES.DASHBOARD}
          <Tooltip
            popupContent={TOOLTIP}
            size="primary"
            placement="bottom"
            alignSelf="center"
          >
            <InfoIcon color="grey400" size="small" />
          </Tooltip>
        </Typography>
      }
      withCompanyName
      additionalComponent={!isOperator && filters}
    >
      {isOperator && filters}
      <div className={styles.content}>
        <InfoBlock
          volumesData={volumesData}
          revenueData={revenueData}
          currency={supplierCurrency}
        />
        <ReservationsSchedule
          isShortPeriod={isShortPeriod}
          carCategories={selectedCarCategories}
        />
        <RevenuePerCarClass
          isShortPeriod={isShortPeriod}
          carCategories={selectedCarCategories}
        />
        <RevenuePerCar carCategories={selectedCarCategories} />
      </div>
    </Container>
  )
}
